import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMenu } from '../../contexts/MenuContext';
import CategoryForm from './CategoryForm';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiGrid } = FiIcons;

const CategoryManagement = () => {
  const { menuCategories, deleteCategory, getItemsByCategory } = useMenu();
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = (categoryId) => {
    const itemsInCategory = getItemsByCategory(categoryId);
    if (itemsInCategory.length > 0) {
      alert(`Cannot delete category "${menuCategories.find(c => c.id === categoryId)?.name}" because it contains ${itemsInCategory.length} item(s). Please move or delete the items first.`);
      return;
    }

    if (window.confirm('Are you sure you want to delete this category?')) {
      const result = deleteCategory(categoryId);
      if (!result.success) {
        alert(result.error);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  const sortedCategories = [...menuCategories].sort((a, b) => 
    (a.sortOrder || 999) - (b.sortOrder || 999)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Category Management</h2>
          <p className="text-warm-brown/70">Manage your menu categories</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {sortedCategories.map((category, index) => {
          const itemCount = getItemsByCategory(category.id).length;
          
          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <div className="p-6">
                <div className="text-center mb-4">
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <h3 className="text-xl font-playfair text-warm-brown font-medium mb-2">
                    {category.name}
                  </h3>
                  <p className="text-warm-brown/70 text-sm">
                    {itemCount} item{itemCount !== 1 ? 's' : ''}
                  </p>
                </div>

                <div className="flex items-center justify-center gap-1 mb-4">
                  <span className="text-xs text-warm-brown/60">Sort Order:</span>
                  <span className="text-sm font-medium text-warm-brown">
                    {category.sortOrder || 'N/A'}
                  </span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(category)}
                    className="flex-1 bg-warm-brown/10 text-warm-brown py-2 px-3 rounded-lg hover:bg-warm-brown/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiEdit2} className="text-sm" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="flex-1 bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                    disabled={itemCount > 0}
                  >
                    <SafeIcon icon={FiTrash2} className="text-sm" />
                    Delete
                  </button>
                </div>

                {itemCount > 0 && (
                  <p className="text-xs text-orange-600 mt-2 text-center">
                    Cannot delete: contains items
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {menuCategories.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiGrid} className="text-6xl text-warm-brown/30 mx-auto mb-4" />
          <p className="text-warm-brown/70 text-lg">No categories found</p>
          <p className="text-warm-brown/50 text-sm">Add your first category to organize your menu!</p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          onClose={resetForm}
        />
      )}
    </div>
  );
};

export default CategoryManagement;